import React from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Users, ClipboardCheck, AlertTriangle, User } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import {
  useTeamStatisticsQuery,
  getUserDisplayName,
  UserStats,
} from "@/hooks/useTeamStatisticsQuery";

const getRoleLabels = (t: (key: string) => string): Record<string, string> => ({
  admin: t("userManagement.roleLabels.admin"),
  tester: t("userManagement.roleLabels.tester"),
  qualitaetsmanager: t("userManagement.roleLabels.qualitaetsmanager"),
  mangelbearbeiter: t("userManagement.roleLabels.mangelbearbeiter"),
  customer_approver: t("userManagement.roleLabels.customer_approver"),
});

const roleColors: Record<string, string> = {
  admin: "bg-destructive/10 text-destructive",
  tester: "bg-info/10 text-info",
  qualitaetsmanager: "bg-success/10 text-success",
  mangelbearbeiter: "bg-warning/10 text-warning",
  customer_approver: "bg-secondary/20 text-secondary-foreground",
};

interface TeamStatisticsProps {
  teamId?: string;
}

export default function TeamStatistics({ teamId }: TeamStatisticsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userStats, totals, isLoading } = useTeamStatisticsQuery(teamId);

  const roleLabels = getRoleLabels(t);

  const handleNavigateToInspections = (userId: string, status?: string) => {
    const params = new URLSearchParams();
    params.set("assignee", userId);
    if (status) params.set("status", status);
    navigate(`/inspections?${params.toString()}`);
  };

  const handleNavigateToDefects = (userId: string, status?: string) => {
    const params = new URLSearchParams();
    params.set("assignee", userId);
    if (status) params.set("status", status);
    navigate(`/defects?${params.toString()}`);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-slide-up">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          {t("teamStatistics.title")}
        </CardTitle>
        <CardDescription>{t("teamStatistics.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid container-stats-4 gap-4 mb-6">
          <div className="text-center p-3 rounded-lg bg-primary/10">
            <p className="text-2xl font-bold text-primary">{totals.inspectionsActive}</p>
            <p className="text-xs text-muted-foreground">{t("teamStatistics.inspectionsActive")}</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-success/10">
            <p className="text-2xl font-bold text-success">{totals.inspectionsCompleted}</p>
            <p className="text-xs text-muted-foreground">
              {t("teamStatistics.inspectionsCompleted")}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-warning/10">
            <p className="text-2xl font-bold text-warning">{totals.defectsOpen}</p>
            <p className="text-xs text-muted-foreground">{t("teamStatistics.defectsOpen")}</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-info/10">
            <p className="text-2xl font-bold text-info">{totals.defectsResolved}</p>
            <p className="text-xs text-muted-foreground">{t("teamStatistics.defectsResolved")}</p>
          </div>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 mb-4">
            <TabsTrigger value="all" className="text-xs sm:text-sm">
              {t("teamStatistics.all")} ({userStats.length})
            </TabsTrigger>
            <TabsTrigger value="qm" className="text-xs sm:text-sm">
              {t("teamStatistics.qm")} (
              {userStats.filter((u) => u.roles.includes("qualitaetsmanager")).length})
            </TabsTrigger>
            <TabsTrigger value="testers" className="text-xs sm:text-sm">
              {t("teamStatistics.testers")} (
              {userStats.filter((u) => u.roles.includes("tester")).length})
            </TabsTrigger>
            <TabsTrigger value="handlers" className="text-xs sm:text-sm">
              {t("teamStatistics.defectHandlers")} (
              {userStats.filter((u) => u.roles.includes("mangelbearbeiter")).length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-3">
            {userStats.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>{t("teamStatistics.noTeamMembers")}</p>
              </div>
            ) : (
              userStats.map((user) => (
                <UserStatCard
                  key={`all-${user.userId}`}
                  user={user}
                  displayName={getUserDisplayName(user)}
                  onInspectionClick={handleNavigateToInspections}
                  onDefectClick={handleNavigateToDefects}
                  roleLabels={roleLabels}
                  t={t}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="qm" className="space-y-3">
            {userStats
              .filter((u) => u.roles.includes("qualitaetsmanager"))
              .map((user) => (
                <UserStatCard
                  key={`qm-${user.userId}`}
                  user={user}
                  displayName={getUserDisplayName(user)}
                  onInspectionClick={handleNavigateToInspections}
                  onDefectClick={handleNavigateToDefects}
                  roleLabels={roleLabels}
                  t={t}
                />
              ))}
          </TabsContent>

          <TabsContent value="testers" className="space-y-3">
            {userStats
              .filter((u) => u.roles.includes("tester"))
              .map((user) => (
                <UserStatCard
                  key={`tester-${user.userId}`}
                  user={user}
                  displayName={getUserDisplayName(user)}
                  showInspections
                  onInspectionClick={handleNavigateToInspections}
                  onDefectClick={handleNavigateToDefects}
                  roleLabels={roleLabels}
                  t={t}
                />
              ))}
          </TabsContent>

          <TabsContent value="handlers" className="space-y-3">
            {userStats
              .filter((u) => u.roles.includes("mangelbearbeiter"))
              .map((user) => (
                <UserStatCard
                  key={`handler-${user.userId}`}
                  user={user}
                  displayName={getUserDisplayName(user)}
                  showDefects
                  onInspectionClick={handleNavigateToInspections}
                  onDefectClick={handleNavigateToDefects}
                  roleLabels={roleLabels}
                  t={t}
                />
              ))}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

interface UserStatCardProps {
  user: UserStats;
  displayName: string;
  showInspections?: boolean;
  showDefects?: boolean;
  onInspectionClick?: (userId: string, status?: string) => void;
  onDefectClick?: (userId: string, status?: string) => void;
  roleLabels: Record<string, string>;
  t: (key: string) => string;
}

const UserStatCard = React.forwardRef<HTMLDivElement, UserStatCardProps>(
  (
    {
      user,
      displayName,
      showInspections = true,
      showDefects = true,
      onInspectionClick,
      onDefectClick,
      roleLabels,
      t,
    },
    ref,
  ) => {
    const hasActiveWork = user.inspections.inProgress > 0 || user.defects.inProgress > 0;

    const inspectionProgress =
      user.inspections.total > 0 ? (user.inspections.completed / user.inspections.total) * 100 : 0;

    const defectProgress =
      user.defects.total > 0
        ? ((user.defects.resolved + user.defects.closed) / user.defects.total) * 100
        : 0;

    return (
      <div
        ref={ref}
        className={`p-4 rounded-lg border ${hasActiveWork ? "bg-primary/5 border-primary/20" : "bg-secondary/30"}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <ProfileAvatar
              profile={{
                first_name: user.firstName,
                last_name: user.lastName,
                email: user.email,
                display_name: user.displayName,
                avatar_url: user.avatarUrl,
              }}
              size="md"
            />
            <div>
              <p className="font-medium" data-sentry-mask>
                {displayName}
              </p>
              <p className="text-xs text-muted-foreground" data-sentry-mask>
                {user.email}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 justify-end">
            {user.roles.map((role) => (
              <Badge key={role} variant="secondary" className={`text-xs ${roleColors[role] || ""}`}>
                {roleLabels[role] || role}
              </Badge>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 container-grid-2 gap-4">
          {showInspections && (
            <div className="space-y-2">
              <div
                className="flex items-center justify-between text-sm cursor-pointer hover:text-primary transition-colors"
                onClick={() => onInspectionClick?.(user.userId)}
              >
                <span className="flex items-center gap-1 text-muted-foreground">
                  <ClipboardCheck className="h-4 w-4" />
                  {t("teamStatistics.inspections")}
                </span>
                <span className="font-medium hover:underline">
                  {user.inspections.completed}/{user.inspections.total}
                </span>
              </div>
              {/* TES-879: progressbar needs an accessible name (WCAG aria-progressbar-name) */}
              <Progress
                value={inspectionProgress}
                className="h-2"
                aria-label={t("teamStatistics.inspections")}
              />
              <div className="flex flex-wrap gap-2 text-xs">
                {user.inspections.inProgress > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs text-info"
                    onClick={() => onInspectionClick?.(user.userId, "in_progress")}
                  >
                    ⏳ {user.inspections.inProgress} {t("teamStatistics.inProgress")}
                  </Button>
                )}
                {user.inspections.pending > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs text-muted-foreground"
                    onClick={() => onInspectionClick?.(user.userId, "pending")}
                  >
                    📋 {user.inspections.pending} {t("teamStatistics.pending")}
                  </Button>
                )}
                {user.inspections.overdue > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs text-destructive"
                    onClick={() => onInspectionClick?.(user.userId, "overdue")}
                  >
                    ⚠️ {user.inspections.overdue} {t("teamStatistics.overdue")}
                  </Button>
                )}
              </div>
            </div>
          )}

          {showDefects && (
            <div className="space-y-2">
              <div
                className="flex items-center justify-between text-sm cursor-pointer hover:text-primary transition-colors"
                onClick={() => onDefectClick?.(user.userId)}
              >
                <span className="flex items-center gap-1 text-muted-foreground">
                  <AlertTriangle className="h-4 w-4" />
                  {t("teamStatistics.defects")}
                </span>
                <span className="font-medium hover:underline">
                  {user.defects.resolved + user.defects.closed}/{user.defects.total}
                </span>
              </div>
              {/* TES-879: progressbar needs an accessible name (WCAG aria-progressbar-name) */}
              <Progress
                value={defectProgress}
                className="h-2"
                aria-label={t("teamStatistics.defects")}
              />
              <div className="flex flex-wrap gap-2 text-xs">
                {user.defects.inProgress > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs text-info"
                    onClick={() => onDefectClick?.(user.userId, "in_progress")}
                  >
                    🔧 {user.defects.inProgress} {t("teamStatistics.inProcessing")}
                  </Button>
                )}
                {user.defects.open > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs text-warning"
                    onClick={() => onDefectClick?.(user.userId, "open")}
                  >
                    🔴 {user.defects.open} {t("teamStatistics.open")}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);
UserStatCard.displayName = "UserStatCard";
